import { useState } from "react";
import { clientsApi, ApiError } from "@/api";
import type { ClientCreateInput } from "@/types";
import { Button, TextField, TextAreaField } from "@/components/ui";
import { FormDrawer } from "../jobs/FormDrawer";

/** Standalone "New client" — creates a client with no job link via POST
 *  {Jobs}/clients/create. Identity required: a company name (when "is a
 *  company") or a first + last name. The server composes `address` from the
 *  split fields and does NOT push to Xero (created lazily at invoice time), so
 *  there's no Xero outcome to surface here. On success the list refetches. */
export function ClientCreateDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [isCompany, setIsCompany] = useState(false);
  const [company, setCompany] = useState("");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [role, setRole] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [line3, setLine3] = useState("");
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [postcode, setPostcode] = useState("");
  const [notes, setNotes] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  /** Only send non-empty fields; the server defaults the rest to "". */
  function buildBody(): ClientCreateInput {
    const body: ClientCreateInput = { is_company: isCompany };
    const put = (key: keyof ClientCreateInput, val: string) => {
      const t = val.trim();
      if (t) (body as Record<string, unknown>)[key] = t;
    };
    put("company_name", company);
    put("contact_first_name", first);
    put("contact_last_name", last);
    put("contact_role", role);
    put("email", email);
    put("phone", phone);
    put("address_line_1", line1);
    put("address_line_2", line2);
    put("address_line_3", line3);
    put("city", city);
    put("county", county);
    if (postcode.trim()) body.postcode = postcode.trim().toUpperCase();
    put("notes", notes);
    return body;
  }

  async function submit() {
    if (saving) return;
    if (isCompany ? !company.trim() : !(first.trim() && last.trim())) {
      setError(isCompany ? "Enter the company name." : "Enter the client's first and last name.");
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      await clientsApi.createClient(buildBody());
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the client.");
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title="New client"
      busy={saving}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving}>Create client</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <section className="job-drawer__section">
        <h3>Contact</h3>
        <label className="lead-drawer__check">
          <input type="checkbox" checked={isCompany} onChange={(e) => setIsCompany(e.target.checked)} />
          This client is a company
        </label>
        {isCompany ? (
          <>
            <TextField label="Company name" required value={company} onChange={(e) => setCompany(e.target.value)} />
            <TextField label="Contact role" value={role} onChange={(e) => setRole(e.target.value)} hint="Optional — the named contact's job title" />
          </>
        ) : null}
        <div className="lead-drawer__two">
          <TextField label="First name" required={!isCompany} value={first} onChange={(e) => setFirst(e.target.value)} />
          <TextField label="Last name" required={!isCompany} value={last} onChange={(e) => setLast(e.target.value)} />
        </div>
        <div className="lead-drawer__two">
          <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
      </section>

      <section className="job-drawer__section">
        <h3>Address</h3>
        <TextField label="Address line 1" value={line1} onChange={(e) => setLine1(e.target.value)} />
        <TextField label="Address line 2" value={line2} onChange={(e) => setLine2(e.target.value)} />
        <TextField label="Address line 3" value={line3} onChange={(e) => setLine3(e.target.value)} />
        <div className="lead-drawer__two">
          <TextField label="City / town" value={city} onChange={(e) => setCity(e.target.value)} />
          <TextField label="County" value={county} onChange={(e) => setCounty(e.target.value)} />
        </div>
        <TextField label="Postcode" value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} />
      </section>

      <section className="job-drawer__section">
        <h3>Notes</h3>
        <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </section>
    </FormDrawer>
  );
}
